import type * as PIXI from 'pixi.js';

export type ViewfinderAspectRatio = '16:9' | '1';

type EditorVersion = 'web_editor_v1' | undefined;
interface ResetEditor {
  type: 'RESET';
  state?: EditorState;
}
interface SetZoomAction {
  type: 'SET_ZOOM';
  zoom: number;
  bounds?: ZoomBounds;
}
interface SetBgColorAction {
  type: 'SET_BG_COLOR';
  color: number;
  showMessage?: boolean;
}
interface ShowBackgroundSelector {
  type: 'SHOW_CUSTOM_BG_SELECTOR';
  value: boolean;
}
interface SetImgPosition {
  type: 'SET_IMG_POSITION';
  /** Normalised position */
  position: Point;
}
interface EditorLoaded {
  type: 'LOADED';
}
interface SetColorSuggestions {
  type: 'COLOR_SUGGESTIONS';
  colors: number[];
}
export type EditorAction =
  | SetZoomAction
  | SetBgColorAction
  | ShowBackgroundSelector
  | SetImgPosition
  | SetColorSuggestions
  | EditorLoaded
  | ResetEditor;

export interface ImageEditorProps {
  /** How high to make the canvas element */
  canvasHeight: number;
  /** Disable the background color selection control */
  disableBgEdit?: boolean;
  /** If true, the editor will take focus when it loads */
  focusOnMount?: boolean;
  /** If true, the image will always cover the viewfinder, and background color will not be visible. */
  imgCover?: boolean;
  /** The original source of the image. */
  imgSrc: string;
  /** Called every time the image changes. The data is a base64 dataURL, low quality */
  // biome-ignore lint/suspicious/noConfusingVoidType: historic
  onLowQualityChanged?: (data: string) => void | undefined;
  /** Called every time a high quality image is generated. */
  // biome-ignore lint/suspicious/noConfusingVoidType: historic
  onHighQualityChanged?: (data: string | undefined) => void | undefined;
  /** Will the editor try and suggest a background color */
  suggestColors?: boolean;
  /** The state of the editor.*/
  editorState: EditorState;
  /** A reducer to update the state of the editor. */

  updateEditorState: React.Dispatch<EditorAction>;
  /** The aspect ratio of the viewfinder. */
  viewfinderAspectRatio: ViewfinderAspectRatio;
  /** Will be multiplied by the initial zoom as a correction. */
  zoomCorrectionCoefficient?: number | undefined;
}

export enum DragState {
  NotDragging = 0,
  DragStarted = 1,
  Dragging = 2,
}

export interface Draggable {
  dragData?: PIXI.FederatedMouseEvent;
  dragging?: DragState;
  dragPointerStart?: PIXI.PointData;
  dragObjStart?: PIXI.Point;
  dragGlobalStart?: PIXI.Point;
}
export interface Point {
  x: number;
  y: number;
}

export interface DragObject extends PIXI.Graphics, Draggable {}
export interface DragSprite extends PIXI.Sprite, Draggable {}

export interface ZoomBounds {
  min: number;
  max: number;
  default: number;
  defaultX: number;
  defaultY: number;
}
export interface EditorState {
  /**
   * The background color for the image.
   * This is not for suggesting colors.
   */
  backgroundColor?: number | undefined | null;
  // enable the background show message
  showCackgroundColorChangingMessage?: boolean | undefined;
  // enable the background show message
  showCustomBackgroundColorSelector?: boolean | undefined;
  /** The version of the editor. Used for compatibility. */
  editorVersion?: EditorVersion;
  /** Set true when the editor has loaded. */
  loaded?: boolean;
  /** Used to store an ID for original image being edited.
   * Use this property to know whether to reset the editor settings.
   */
  originalImageReference?: string | undefined;
  /**
   * The position of the center of the image.
   * The editor will place the image in the center of the viewfinder if null or undefined.
   * This should be the NORMALIZED position.
   * The center of the editor is (0,0)
   * Top left: (-1, -1)
   * Bottom right: (1, 1)
   */
  position?: Point | undefined | null;
  /** The colors suggested by the color suggestor hook */
  suggestedbackgroundColors?: number[] | undefined;
  /**
   *  The scaling factor of the input image
   *  Editor will auto-discover a sensible value if null or undefined will
   */
  zoom?: number | undefined | null;
  /**
   * The min/max values for zoom.
   * Editor will discover reasonable values if not defined.
   */
  zoomBounds?: ZoomBounds | undefined | null;
}
